import React, { useState, useContext, useEffect } from 'react';
import { UserContext } from '../../../context/userContext';
import { ManageRegistrationMode } from '../../../types/appTypes';
import { Company, Project } from '../../../types/appModels';
import { useData } from '../../../hooks/useData';
import { 
  FiUser, 
  FiMail, 
  FiHome, 
  FiShield, 
  FiKey, 
  FiCheck, 
  FiX,
  FiInfo
} from 'react-icons/fi';
import css from './AdminRegistrationForm.module.css';

interface AdminRegistrationFormProps {
  mode: ManageRegistrationMode;
  dataId?: string;
  profileId?: string;
  onSuccess?: () => void;
}

// Define tooltip content for user types
const userTypeTooltips = {
  'Lite': 'Restricted access (e.g. water transfer)',
  'User': 'Default access with regular permissions',
  'Ambassador': 'Upgraded privilege of managing that company\'s users',
  'Manager': 'Leads and above; can manage document store',
  'Admin': 'Administrative access'
};

const AdminRegistrationForm = ({
  mode,
  dataId,
  profileId,
  onSuccess,
}: AdminRegistrationFormProps) => {
  const { user } = useContext(UserContext);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    companyId: '',
    company: '',
    role: '',
    userType: '',
    apiType: '',
    apiData: [] as string[],
    apiCompanyIds: [] as string[],
    apiCompanies: [] as string[],
    approveApi: true,
    approveAtlas: true,
    genNewKey: false,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const [companyFilter, setCompanyFilter] = useState('');

  // Effect to set default userType based on role
  useEffect(() => {
    if (formData.role && !formData.userType) {
      setFormData(prev => ({
        ...prev,
        userType: formData.role
      }));
    }
  }, [formData.role]);

  // Fetch company data
  const { dataState: companyDataState } = useData<Company[]>({
    endpoint: `${process.env.REACT_APP_API_COMPANIES}?top=false`,
    initialData: [],
  });

  // Fetch project data when needed
  const { dataState: projectsDataState, reloadData: reloadProjects } = useData<Project[]>({
    endpoint: process.env.REACT_APP_API_PROJECT_SEARCH!,
    method: 'POST',
    initialData: [],
    loadWhen: formData.apiType === 'Pad' && formData.apiCompanies.length > 0,
    body: JSON.stringify({
      top: false,
      companies: formData.apiCompanies,
    }),
    onComplete: (data, status, error) => {
      if (formData.apiType === 'Pad' && data) {
        // Get unique pad names and maintain their order
        const uniquePads = Array.from(new Set(data.map(project => project.padName)));
        
        // Update form data with unique pad names
        setFormData(prev => ({
          ...prev,
          apiData: prev.apiData.filter(padName => uniquePads.includes(padName))
        }));
      }
    }
  });

  // Get unique pads for rendering
  const uniquePads = React.useMemo(() => {
    if (!projectsDataState.data) return [];
    return Array.from(new Set(projectsDataState.data.map(project => project.padName)))
      .sort((a, b) => a.localeCompare(b));
  }, [projectsDataState.data]);

  const filteredCompanies = companyDataState.data?.filter(company => 
    company.name.toLowerCase().includes(companyFilter.toLowerCase())
  ) || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }

    // If role is changing, auto-set userType based on role
    if (name === 'role') {
      if (value === 'Manager' || value === 'Admin') {
        setFormData(prev => ({
          ...prev,
          userType: value
        }));
      } else if (value === 'User') {
        setFormData(prev => ({
          ...prev,
          userType: 'User' // Default to 'User' when role is User
        }));
      }
    }
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name } = e.target;
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    
    setFormData(prev => ({
      ...prev,
      [name]: selectedOptions,
    }));
  };

  const handleApiTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      apiType: value,
      apiData: [], // Clear pad selection when changing API type
    }));

    // If changing to Pad type and companies are selected, reload projects
    if (value === 'Pad' && formData.apiCompanies.length > 0) {
      reloadProjects();
    }
  };

  const handleApiCompaniesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name } = e.target;
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    const selectedCompanyIds = Array.from(e.target.selectedOptions, option => option.getAttribute('data-company-id') || '');
    
    setFormData(prev => ({
      ...prev,
      [name]: selectedOptions,
      apiCompanyIds: selectedCompanyIds,
      apiData: [], // Clear pad selection when companies change
    }));

    // Always reload projects data if API type is Pad
    if (formData.apiType === 'Pad') {
      reloadProjects();
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else {
      const lowerEmail = formData.email.toLowerCase();
      if (!/\S+@\S+\.\S+/.test(lowerEmail)) {
        errors.email = 'Please enter a valid email';
      }
      // Update the email in formData to be lowercase
      setFormData(prev => ({
        ...prev,
        email: lowerEmail
      }));
    }

    if (!formData.name) {
      errors.name = 'Name is required';
    }

    if (formData.approveAtlas) {
      if (!formData.companyId) {
        errors.companyId = 'Company is required';
      }
      if (!formData.role) {
        errors.role = 'Role is required';
      }
      if (!formData.userType) {
        errors.userType = 'User type is required';
      }
    }

    if (formData.approveApi) {
      if (!formData.apiType) {
        errors.apiType = 'API type is required';
      }
      if (formData.apiCompanies.length === 0) {
        errors.apiCompanies = 'At least one company is required';
      }
      if (formData.apiType === 'Pad' && formData.apiData.length === 0) {
        errors.apiData = 'At least one pad is required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
  
    setIsSubmitting(true);
    setSubmitError(null);
  
    let apiDataToSend = formData.apiData;
    if (formData.apiType === 'Company' && formData.apiData.length === 0) {
      apiDataToSend = formData.apiCompanies;
    }
  
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_REGISTRATION_ADMIN}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + user!.accessToken,
          },
          body: JSON.stringify({
            regEmail: formData.email,
            name: formData.name,
            apiRequest: formData.approveApi,
            atlasRequest: formData.approveAtlas,
            approveApi: formData.approveApi,
            approveAtlas: formData.approveAtlas,
            companyId: formData.companyId,
            company: formData.company,
            role: formData.role,
            userType: formData.userType,
            apiType: formData.apiType || null,
            apiData: formData.approveApi && formData.apiType ? 
                    (apiDataToSend.length === 0 ? null : apiDataToSend) : 
                    null,
            apiCompanies: formData.apiCompanies,
            apiCompanyIds: formData.apiCompanyIds,
          }),
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        onSuccess?.();
      } else {
        setSubmitError(data.message || 'An error occurred while creating the registration');
      }
    } catch (error) {
      setSubmitError('An error occurred while creating the registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to show tooltip on hover
  const handleTooltipShow = (userType: string) => {
    setShowTooltip(userType);
  };

  // Function to hide tooltip
  const handleTooltipHide = () => {
    setShowTooltip(null);
  };

  // Function to get user type options based on role
  const getUserTypeOptions = () => {
    if (formData.role === 'Admin') {
      return [{ value: 'Admin', label: 'Admin' }];
    } else if (formData.role === 'Manager') {
      return [{ value: 'Manager', label: 'Manager' }];
    } else if (formData.role === 'User') {
      return [
        { value: 'Lite', label: 'Lite' },
        { value: 'User', label: 'Default User' },
        { value: 'Ambassador', label: 'Ambassador' }
      ];
    }
    return [];
  };

  return (
    <form onSubmit={handleSubmit} className={css.form}>
      <div className={css.formSection}>
        <h3 className={css.sectionTitle}>
          <FiUser className={css.sectionIcon} />
          User Information
        </h3>
        <div className={css.inputRow}>
          <div className={css.inputGroup}>
            <label className={css.label}>
              <FiMail className={css.inputIcon} />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`${css.input} ${formErrors.email ? css.inputError : ''}`}
              placeholder="Enter email address"
            />
            {formErrors.email && <span className={css.errorMessage}>{formErrors.email}</span>}
          </div>

          <div className={css.inputGroup}>
            <label className={css.label}>
              <FiUser className={css.inputIcon} />
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`${css.input} ${formErrors.name ? css.inputError : ''}`}
              placeholder="Enter full name"
            />
            {formErrors.name && <span className={css.errorMessage}>{formErrors.name}</span>}
          </div>
        </div>
      </div>

      <div className={css.formSection}>
        <h3 className={css.sectionTitle}>
          <FiShield className={css.sectionIcon} />
          Access Permissions
        </h3>
        <div className={css.checkboxGroup}>
          <label className={css.checkboxLabel}>
            <input
              type="checkbox"
              name="approveAtlas"
              checked={formData.approveAtlas}
              onChange={handleInputChange}
              className={css.checkbox}
            />
            <span className={css.checkboxText}>Approve Atlas Access</span>
          </label>

          <label className={css.checkboxLabel}>
            <input
              type="checkbox"
              name="approveApi"
              checked={formData.approveApi}
              onChange={handleInputChange}
              className={css.checkbox}
            />
            <span className={css.checkboxText}>Approve API Access</span>
          </label>
        </div>
      </div>

      {formData.approveAtlas && (
        <div className={css.formSection}>
          <h3 className={css.sectionTitle}>
            <FiHome className={css.sectionIcon} />
            Atlas Settings
          </h3>
          <div className={css.atlasSettingsRow}>
            <div className={css.inputGroup}>
              <label className={css.label}>
                <FiHome className={css.inputIcon} />
                Company
              </label>
              <input
                type="text"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className={css.input}
                placeholder="Filter companies..."
              />
              <select
                name="companyId"
                value={formData.companyId}
                onChange={handleInputChange}
                className={`${css.select} ${formErrors.companyId ? css.inputError : ''}`}
              >
                <option value="">Select a company</option>
                {filteredCompanies.map(company => (
                  <option key={company.companyId} value={company.companyId}>
                    {company.name}
                  </option>
                ))}
              </select>
              {formErrors.companyId && <span className={css.errorMessage}>{formErrors.companyId}</span>}
            </div>

            <div className={css.inputGroup}>
              <label className={css.label}>
                <FiShield className={css.inputIcon} />
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`${css.select} ${formErrors.role ? css.inputError : ''}`}
              >
                <option value="">Select a role</option>
                <option value="User">User</option>
                <option value="Manager">Manager</option>
                <option value="Admin">Admin</option>
              </select>
              {formErrors.role && <span className={css.errorMessage}>{formErrors.role}</span>}

              {formData.role && (
                <div className={css.inputGroup}>
                  <label className={css.label}>
                    <FiShield className={css.inputIcon} />
                    User Type
                    <FiInfo 
                      className={css.infoIcon} 
                      onMouseEnter={() => setShowTooltip('all')}
                      onMouseLeave={handleTooltipHide}
                    />
                  </label>
                  <select
                    name="userType"
                    value={formData.userType}
                    onChange={handleInputChange}
                    className={`${css.select} ${formErrors.userType ? css.inputError : ''}`}
                  >
                    <option value="">Select a user type</option>
                    {getUserTypeOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.userType && <span className={css.errorMessage}>{formErrors.userType}</span>}
                  {showTooltip && (
                    <div className={css.tooltip}>
                      <ul>
                        {Object.entries(userTypeTooltips).map(([type, description]) => (
                          <li key={type}>
                            <strong>{type}:</strong> {description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {formData.approveApi && (
        <div className={css.formSection}>
          <h3 className={css.sectionTitle}>
            <FiKey className={css.sectionIcon} />
            API Settings
          </h3>
          <div className={css.inputGroup}>
            <label className={css.label}>
              <FiKey className={css.inputIcon} />
              API Type
            </label>
            <select
              name="apiType"
              value={formData.apiType}
              onChange={handleApiTypeChange}
              className={`${css.select} ${formErrors.apiType ? css.inputError : ''}`}
            >
              <option value="">Select API type</option>
              <option value="Company">Company</option>
              <option value="Pad">Pad</option>
            </select>
            {formErrors.apiType && <span className={css.errorMessage}>{formErrors.apiType}</span>}
          </div>

          <div className={css.apiSettingsRow}>
            <div className={css.inputGroup}>
              <label className={css.label}>
                <FiHome className={css.inputIcon} />
                API Companies
              </label>
              <select
                name="apiCompanies"
                multiple
                value={formData.apiCompanies}
                onChange={handleApiCompaniesChange}
                className={`${css.select} ${css.multiSelect} ${formErrors.apiCompanies ? css.inputError : ''}`}
              >
                {filteredCompanies.map(company => (
                  <option 
                    key={company.companyId} 
                    value={company.name}
                    data-company-id={company.companyId}
                  >
                    {company.name}
                  </option>
                ))}
              </select>
              {formErrors.apiCompanies && <span className={css.errorMessage}>{formErrors.apiCompanies}</span>}
            </div>

            {formData.apiType === 'Pad' && (
              <div className={css.inputGroup}>
                <label className={css.label}>
                  <FiKey className={css.inputIcon} />
                  Pads
                </label>
                {uniquePads.length === 0 ? (
                  <div className={css.noPadsMessage}>No pads available for selected companies</div>
                ) : (
                  <select
                    name="apiData"
                    multiple
                    value={formData.apiData}
                    onChange={handleMultiSelectChange}
                    className={`${css.select} ${css.multiSelect} ${formErrors.apiData ? css.inputError : ''}`}
                  >
                    {uniquePads.map(padName => (
                      <option 
                        key={padName} 
                        value={padName}
                        className={css.selectOption}
                      >
                        {padName}
                      </option>
                    ))}
                  </select>
                )}
                {formErrors.apiData && <span className={css.errorMessage}>{formErrors.apiData}</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {submitError && (
        <div className={css.errorContainer}>
          <FiX className={css.errorIcon} />
          <span className={css.errorMessage}>{submitError}</span>
        </div>
      )}

      <div className={css.buttonGroup}>
        <button
          type="submit"
          className={css.submitButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className={css.loading}>Creating...</span>
          ) : (
            <>
              <FiCheck className={css.buttonIcon} />
              Create Registration
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default AdminRegistrationForm;